import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  CreditCard,
  Clock,
  Users,
  TrendingUp,
  Settings,
  Plus,
  Calendar,
  AlertCircle,
  CheckCircle,
  DollarSign
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { formatCurrency, UserTier } from '@/lib/index';
import { toast } from 'sonner';
import { Layout } from '@/components/Layout';

interface CreditSummary {
  user: {
    id: string;
    username: string;
    tier: UserTier;
    credit_limit: number;
    credit_reset_time: string;
  };
  totalCreditLimit: number;
  totalAllocated: number;
  remainingAllocatable: number;
  availableToday: number;
  allocations: Array<{
    id: string;
    allocated_amount: number;
    child_user_id: string;
    users: {
      username: string;
      tier: UserTier;
    };
  }>;
  usageHistory: Array<{
    usage_date: string;
    total_bets_amount: number;
    credit_limit_at_date: number;
  }>;
}

interface AvailableUser {
  id: string;
  username: string;
  tier: UserTier;
  current_credit: number;
}

export default function CreditManagement() {
  const { user, isCompany, isAgent } = useAuth();
  const [creditSummary, setCreditSummary] = useState<CreditSummary | null>(null);
  const [availableUsers, setAvailableUsers] = useState<AvailableUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAllocateDialogOpen, setIsAllocateDialogOpen] = useState(false);
  const [isResetTimeDialogOpen, setIsResetTimeDialogOpen] = useState(false);
  const [isManualResetDialogOpen, setIsManualResetDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AvailableUser | null>(null);
  const [allocationAmount, setAllocationAmount] = useState('');
  const [newResetTime, setNewResetTime] = useState('00:00');
  const [resetType, setResetType] = useState<'everyone' | 'specific'>('everyone');
  const [resetTargetUser, setResetTargetUser] = useState<AvailableUser | null>(null);

  // Load credit summary
  const loadCreditSummary = async () => {
    if (!user?.id) return;

    try {
      setIsLoading(true);
      
      const { data, error } = await supabase.functions.invoke(
        `credit_management_2026_02_10_12_05?action=credit_summary&userId=${user.id}`,
        { method: 'GET' }
      );

      if (error) {
        console.error('Error loading credit summary:', error);
        toast.error('Failed to load credit summary');
        return;
      }

      if (data.success) {
        setCreditSummary(data.creditSummary);
        setNewResetTime(data.creditSummary.user.credit_reset_time.substring(0, 5)); // HH:MM format
      }
    } catch (error) {
      console.error('Failed to load credit summary:', error);
      toast.error('Failed to load credit summary');
    } finally {
      setIsLoading(false);
    }
  };

  // Load available users for allocation
  const loadAvailableUsers = async () => {
    if (!user?.id) return;

    try {
      const { data, error } = await supabase.functions.invoke(
        `user_management_v2_2026_02_10_10_15?action=list`,
        { method: 'GET' }
      );

      if (error) {
        console.error('Error loading users:', error);
        return;
      }

      if (data.success) {
        // Filter users that can receive credit allocation
        const eligibleUsers = data.users.filter((u: any) => 
          u.id !== user.id && 
          (u.parent_id === user.id || (isCompany() && u.tier !== 'COMPANY'))
        ).map((u: any) => ({
          id: u.id,
          username: u.username,
          tier: u.tier,
          current_credit: u.credit_limit || 0
        }));

        setAvailableUsers(eligibleUsers);
      }
    } catch (error) {
      console.error('Failed to load available users:', error);
    }
  };

  useEffect(() => {
    if (user) {
      loadCreditSummary();
      loadAvailableUsers();
    }
  }, [user]);

  // Handle credit allocation
  const handleAllocateCredit = async () => {
    if (!selectedUser || !allocationAmount || !user?.id) return;

    const amount = parseFloat(allocationAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    if (amount > (creditSummary?.remainingAllocatable || 0)) {
      toast.error('Amount exceeds available allocatable credit');
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke(
        'credit_management_2026_02_10_12_05?action=allocate_credit',
        {
          method: 'POST',
          body: {
            parentId: user.id,
            childId: selectedUser.id,
            amount: amount
          }
        }
      );

      if (error || !data.success) {
        toast.error(data?.error || 'Failed to allocate credit');
        return;
      }

      toast.success(`Successfully allocated ${formatCurrency(amount)} to ${selectedUser.username}`);
      setIsAllocateDialogOpen(false);
      setSelectedUser(null);
      setAllocationAmount('');
      
      // Reload data
      await loadCreditSummary();
      await loadAvailableUsers();
    } catch (error) {
      console.error('Failed to allocate credit:', error);
      toast.error('Failed to allocate credit');
    }
  };

  // Handle reset time update
  const handleUpdateResetTime = async () => {
    if (!user?.id || !isCompany()) return;

    try {
      const { data, error } = await supabase.functions.invoke(
        'credit_management_2026_02_10_12_05?action=update_reset_time',
        {
          method: 'PUT',
          body: {
            userId: user.id,
            resetTime: newResetTime + ':00' // Add seconds
          }
        }
      );

      if (error || !data.success) {
        toast.error(data?.error || 'Failed to update reset time');
        return;
      }

      toast.success('Credit reset time updated successfully');
      setIsResetTimeDialogOpen(false);
      await loadCreditSummary();
    } catch (error) {
      console.error('Failed to update reset time:', error);
      toast.error('Failed to update reset time');
    }
  };

  // Handle manual credit reset
  const handleManualReset = async () => {
    if (!user?.id || !isCompany()) return;

    try {
      const { data, error } = await supabase.functions.invoke(
        'manual_credit_reset_2026_02_10_12_30?action=manual_reset',
        {
          method: 'POST',
          body: {
            companyUserId: user.id,
            resetType: resetType,
            targetUserId: resetType === 'specific' ? resetTargetUser?.id : null
          }
        }
      );

      if (error || !data.success) {
        toast.error(data?.error || 'Failed to perform manual reset');
        return;
      }

      const message = resetType === 'everyone' 
        ? `Credit limits reset for all ${data.affectedUsers?.length || 0} users`
        : `Credit limits reset for ${resetTargetUser?.username} and ${data.affectedUsers?.length || 0} subordinates`;
      
      toast.success(message);
      setIsManualResetDialogOpen(false);
      setResetType('everyone');
      setResetTargetUser(null);
      
      // Reload data to reflect the reset
      await loadCreditSummary();
    } catch (error) {
      console.error('Failed to perform manual reset:', error);
      toast.error('Failed to perform manual reset');
    }
  };


  // Calculate usage percentage
  const usagePercentage = useMemo(() => {
    if (!creditSummary) return 0;
    const used = creditSummary.totalCreditLimit - creditSummary.availableToday;
    return (used / creditSummary.totalCreditLimit) * 100;
  }, [creditSummary]);

  // Calculate allocation percentage
  const allocationPercentage = useMemo(() => {
    if (!creditSummary) return 0;
    return (creditSummary.totalAllocated / creditSummary.totalCreditLimit) * 100;
  }, [creditSummary]);

  if (!isCompany() && !isAgent()) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold">Access Restricted</h3>
          <p className="text-muted-foreground">Only Company and Agent users can manage credit limits.</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading credit information...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Credit Management</h1>
          <p className="text-muted-foreground">
            Manage 24-hour credit limits and allocations
          </p>
        </div>
        <div className="flex gap-2">
          {isCompany() && (
            <Dialog open={isResetTimeDialogOpen} onOpenChange={setIsResetTimeDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline">
                  <Clock className="mr-2 h-4 w-4" />
                  Reset Time
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Set Credit Reset Time</DialogTitle>
                  <DialogDescription>
                    Configure when daily credit limits reset (24-hour cycle)
                  </DialogDescription>
                </DialogHeader>
                <div className="py-4">
                  <Label htmlFor="reset-time">Reset Time (24-hour format)</Label>
                  <Input
                    id="reset-time"
                    type="time"
                    value={newResetTime}
                    onChange={(e) => setNewResetTime(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Credit limits will reset daily at this time
                  </p>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsResetTimeDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleUpdateResetTime}>
                    Update Reset Time
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}

          {isCompany() && (
            <Dialog open={isManualResetDialogOpen} onOpenChange={setIsManualResetDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="text-orange-600 border-orange-600 hover:bg-orange-50">
                  <AlertCircle className="mr-2 h-4 w-4" />
                  Manual Reset
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Manual Credit Reset</DialogTitle>
                  <DialogDescription>
                    Immediately reset credit limits for selected users. This action cannot be undone.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div>
                    <Label>Reset Type</Label>
                    <div className="flex gap-4 mt-2">
                      <label className="flex items-center space-x-2">
                        <input
                          type="radio"
                          name="resetType"
                          value="everyone"
                          checked={resetType === 'everyone'}
                          onChange={(e) => setResetType(e.target.value as 'everyone' | 'specific')}
                        />
                        <span>Reset Everyone</span>
                      </label>
                      <label className="flex items-center space-x-2">
                        <input
                          type="radio"
                          name="resetType"
                          value="specific"
                          checked={resetType === 'specific'}
                          onChange={(e) => setResetType(e.target.value as 'everyone' | 'specific')}
                        />
                        <span>Reset Specific Agent</span>
                      </label>
                    </div>
                  </div>
                  {resetType === 'specific' && (
                    <div>
                      <Label htmlFor="reset-target">Select Agent to Reset</Label>
                      <select
                        id="reset-target"
                        className="w-full p-2 border rounded-md mt-1"
                        value={resetTargetUser?.id || ''}
                        onChange={(e) => {
                          const user = availableUsers.find(u => u.id === e.target.value && u.tier === 'AGENT');
                          setResetTargetUser(user || null);
                        }}
                      >
                        <option value="">Choose an agent...</option>
                        {availableUsers.filter(u => u.tier === 'AGENT').map(user => (
                          <option key={user.id} value={user.id}>
                            {user.username} (Agent) - Current Credit: {formatCurrency(user.current_credit)}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-muted-foreground mt-1">
                        This will reset the selected agent and all their subordinates
                      </p>
                    </div>
                  )}
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-4 w-4 text-orange-600 mt-0.5" />
                      <div className="text-sm">
                        <p className="font-medium text-orange-800">Warning</p>
                        <p className="text-orange-700">
                          {resetType === 'everyone' 
                            ? 'This will immediately reset credit limits for ALL users in the system.'
                            : 'This will immediately reset credit limits for the selected agent and all their subordinates.'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsManualResetDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleManualReset} 
                    disabled={resetType === 'specific' && !resetTargetUser}
                    className="bg-orange-600 hover:bg-orange-700"
                  >
                    Confirm Reset
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}

          {isCompany() && (
            <Dialog open={isManualResetDialogOpen} onOpenChange={setIsManualResetDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="text-orange-600 border-orange-600 hover:bg-orange-50">
                  <AlertCircle className="mr-2 h-4 w-4" />
                  Manual Reset
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Manual Credit Reset</DialogTitle>
                  <DialogDescription>
                    Immediately reset credit limits for selected users. This action cannot be undone.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div>
                    <Label>Reset Type</Label>
                    <div className="flex gap-4 mt-2">
                      <label className="flex items-center space-x-2">
                        <input
                          type="radio"
                          name="resetType"
                          value="everyone"
                          checked={resetType === 'everyone'}
                          onChange={(e) => setResetType(e.target.value as 'everyone' | 'specific')}
                        />
                        <span>Reset Everyone</span>
                      </label>
                      <label className="flex items-center space-x-2">
                        <input
                          type="radio"
                          name="resetType"
                          value="specific"
                          checked={resetType === 'specific'}
                          onChange={(e) => setResetType(e.target.value as 'everyone' | 'specific')}
                        />
                        <span>Reset Specific Agent</span>
                      </label>
                    </div>
                  </div>
                  {resetType === 'specific' && (
                    <div>
                      <Label htmlFor="reset-target">Select Agent to Reset</Label>
                      <select
                        id="reset-target"
                        className="w-full p-2 border rounded-md mt-1"
                        value={resetTargetUser?.id || ''}
                        onChange={(e) => {
                          const user = availableUsers.find(u => u.id === e.target.value && u.tier === 'AGENT');
                          setResetTargetUser(user || null);
                        }}
                      >
                        <option value="">Choose an agent...</option>
                        {availableUsers.filter(u => u.tier === 'AGENT').map(user => (
                          <option key={user.id} value={user.id}>
                            {user.username} (Agent) - Current Credit: {formatCurrency(user.current_credit)}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-muted-foreground mt-1">
                        This will reset the selected agent and all their subordinates
                      </p>
                    </div>
                  )}
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-4 w-4 text-orange-600 mt-0.5" />
                      <div className="text-sm">
                        <p className="font-medium text-orange-800">Warning</p>
                        <p className="text-orange-700">
                          {resetType === 'everyone' 
                            ? 'This will immediately reset credit limits for ALL users in the system.'
                            : 'This will immediately reset credit limits for the selected agent and all their subordinates.'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsManualResetDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleManualReset} 
                    disabled={resetType === 'specific' && !resetTargetUser}
                    className="bg-orange-600 hover:bg-orange-700"
                  >
                    Confirm Reset
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
          
          <Dialog open={isAllocateDialogOpen} onOpenChange={setIsAllocateDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                Allocate Credit
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Allocate Credit Limit</DialogTitle>
                <DialogDescription>
                  Distribute your credit limit to subordinate users
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label htmlFor="user-select">Select User</Label>
                  <select
                    id="user-select"
                    className="w-full p-2 border rounded-md"
                    value={selectedUser?.id || ''}
                    onChange={(e) => {
                      const user = availableUsers.find(u => u.id === e.target.value);
                      setSelectedUser(user || null);
                    }}
                  >
                    <option value="">Choose a user...</option>
                    {availableUsers.map(user => (
                      <option key={user.id} value={user.id}>
                        {user.username} ({user.tier}) - Current: {formatCurrency(user.current_credit)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="amount">Allocation Amount (RM)</Label>
                  <Input
                    id="amount"
                    type="number"
                    min="0"
                    step="0.01"
                    max={creditSummary?.remainingAllocatable || 0}
                    value={allocationAmount}
                    onChange={(e) => setAllocationAmount(e.target.value)}
                    placeholder="0.00"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Available to allocate: {formatCurrency(creditSummary?.remainingAllocatable || 0)}
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsAllocateDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleAllocateCredit} disabled={!selectedUser || !allocationAmount}>
                  Allocate Credit
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Credit Overview Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Credit Limit</CardTitle>
            <CreditCard className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(creditSummary?.totalCreditLimit || 0)}</div>
            <p className="text-xs text-muted-foreground">
              Resets at {creditSummary?.user.credit_reset_time.substring(0, 5)} daily
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Available Today</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {formatCurrency(creditSummary?.availableToday || 0)}
            </div>
            <Progress value={100 - usagePercentage} className="mt-2" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Allocated to Others</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(creditSummary?.totalAllocated || 0)}</div>
            <Progress value={allocationPercentage} className="mt-2" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Remaining to Allocate</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {formatCurrency(creditSummary?.remainingAllocatable || 0)}
            </div>
            <p className="text-xs text-muted-foreground">
              Available for distribution
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Credit Allocations Table */}
      {creditSummary?.allocations && creditSummary.allocations.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Credit Allocations</CardTitle>
            <CardDescription>
              Credit limits allocated to your subordinate users
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Tier</TableHead>
                  <TableHead>Allocated Amount</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {creditSummary.allocations.map((allocation) => (
                  <TableRow key={allocation.id}>
                    <TableCell className="font-medium">
                      {allocation.users.username}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {allocation.users.tier}
                      </Badge>
                    </TableCell>
                    <TableCell>{formatCurrency(allocation.allocated_amount)}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-green-600 border-green-600">
                        Active
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Usage History */}
      {creditSummary?.usageHistory && creditSummary.usageHistory.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Recent Usage History</CardTitle>
            <CardDescription>
              Daily credit usage over the past 7 days
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Credit Limit</TableHead>
                  <TableHead>Amount Used</TableHead>
                  <TableHead>Usage %</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {creditSummary.usageHistory.map((usage) => {
                  const usagePercent = (usage.total_bets_amount / usage.credit_limit_at_date) * 100;
                  return (
                    <TableRow key={usage.usage_date}>
                      <TableCell>{new Date(usage.usage_date).toLocaleDateString()}</TableCell>
                      <TableCell>{formatCurrency(usage.credit_limit_at_date)}</TableCell>
                      <TableCell>{formatCurrency(usage.total_bets_amount)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Progress value={usagePercent} className="w-16" />
                          <span className="text-sm">{usagePercent.toFixed(1)}%</span>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
      </div>
    </Layout>
  );
}